import { useEffect, useRef, useState } from 'react';
import {
  ReaderIcon,
  Cross2Icon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ReloadIcon,
  MagnifyingGlassIcon,
  LockClosedIcon,
} from '@radix-ui/react-icons';
import type { EbookClient } from '../services/ebooks';
import { EbookShelfCover } from './EbookShelfCover';
import { EbookBookModel } from './EbookBookModel';
import { EbookShelfLock } from './EbookShelfLock';
import { useBookshelfRail } from './useBookshelfRail';
import './student-bookshelf.css';

export interface BookshelfViewState {
  selectedId: string;
  firstVisibleId: string | null;
  library?: {
    open: boolean;
    query: string;
    filter: 'ALL' | 'AVAILABLE' | 'LOCKED';
    limit: number;
    scrollTop: number;
  };
}
export interface StudentBookshelfProps {
  items: Awaited<ReturnType<EbookClient['list']>>;
  client: EbookClient;
  initialBookId?: string | null;
  initialView?: BookshelfViewState | null;
  onViewChange?(view: BookshelfViewState): void;
  busy: boolean;
  error: string;
  onOpen(id: string): void;
  onRefresh(): void;
}
type Book = StudentBookshelfProps['items'][number];
export function shelfCompletion(item: Book) {
  if (item.accessStatus === 'LOCKED' || item.completedCount === undefined) return null;
  const total = Math.max(0, item.pageCount);
  const completed = Math.min(total, Math.max(0, item.completedCount));
  return { completed, total, percent: total ? Math.round((completed / total) * 100) : 0 };
}
function Cover({
  item,
  client,
  variant,
  shake = 0,
}: {
  item: Book;
  client: EbookClient;
  variant?: 'shelf';
  shake?: number;
}) {
  const path =
    item.coverImage && item.coverDocumentId
      ? `/catalog/${item.coverDocumentId}/cover`
      : item.coverImage && item.coverReleaseId
        ? `/releases/${item.coverReleaseId}/cover`
        : null;
  if (variant)
    return (
      <EbookBookModel
        key={item.id}
        client={client}
        title={item.name}
        path={path}
        variant={variant}
        decoration={item.accessStatus === 'LOCKED' ? <EbookShelfLock shake={shake} /> : undefined}
      />
    );
  return <EbookShelfCover client={client} title={item.name} path={path} />;
}
function Progress({ item, compact = false }: { item: Book; compact?: boolean }) {
  const locked = item.accessStatus === 'LOCKED';
  const progress = shelfCompletion(item);
  return (
    <div className="student-bookshelf__completion">
      {!compact && (
        <div className="student-bookshelf__progress-label">
          <span>完成进度</span>
          <span>{locked ? '—' : progress ? `${progress.percent}%` : '暂未同步'}</span>
        </div>
      )}
      <div
        className={`student-bookshelf__progress${locked ? ' is-locked' : ''}`}
        role={progress ? 'progressbar' : undefined}
        aria-label={`${item.name}完成率`}
        aria-valuenow={progress?.percent}
        aria-valuemin={progress ? 0 : undefined}
        aria-valuemax={progress ? 100 : undefined}
      >
        <span style={{ transform: `scaleX(${(progress?.percent ?? 0) / 100})` }} />
      </div>
      <div className="student-bookshelf__meta">
        <span>
          {locked
            ? `共 ${item.pageCount} 题`
            : progress
              ? `${progress.completed} / ${progress.total} 题`
              : `${item.pageCount} 道练习题`}
        </span>
        {compact ? (
          <span>{locked ? '—' : progress ? `${progress.percent}%` : '暂未同步'}</span>
        ) : (
          !locked && !progress && <span>进度暂未同步</span>
        )}
      </div>
    </div>
  );
}
function ReviewCount({ item }: { item: Book }) {
  if (item.accessStatus === 'LOCKED')
    return (
      <span className="student-bookshelf__review student-bookshelf__review--locked">
        <LockClosedIcon aria-hidden="true" />
        待解锁
      </span>
    );
  return (
    <span className="student-bookshelf__review">
      <span className="student-bookshelf__review-label">
        <ReloadIcon aria-hidden="true" />
        待复习
      </span>
      {item.pendingReviewCount === undefined ? (
        <span className="student-bookshelf__review-unavailable">暂未同步</span>
      ) : (
        <span className="student-bookshelf__review-count">
          <b>{item.pendingReviewCount}</b> 题
        </span>
      )}
    </span>
  );
}
export function StudentBookshelf({
  items,
  client,
  busy,
  error,
  onOpen,
  onRefresh,
  initialBookId,
  initialView,
  onViewChange,
}: StudentBookshelfProps) {
  const [selectedId, setSelectedId] = useState(initialBookId ?? initialView?.selectedId ?? '');
  const [active, setActive] = useState<string | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<NonNullable<BookshelfViewState['library']>>(
    initialView?.library ?? { open: false, query: '', filter: 'ALL', limit: 30, scrollTop: 0 },
  );
  const { open: library, query, filter, limit } = catalog;
  const [closing, setClosing] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const catalogScroll = useRef(catalog.scrollTop);
  const scrollArea = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const [notice, setNotice] = useState<{ id: string; sequence: number } | null>(null);
  const noticeSequence = useRef(0);
  const noticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const position = useRef(
    Math.max(
      0,
      items.findIndex((item) => item.id === initialView?.firstVisibleId),
    ),
  );
  const firstId = useRef(initialView?.firstVisibleId ?? items[0]?.id ?? null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const libraryButton = useRef<HTMLButtonElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const latest = useRef({ busy, opening, onOpen, onViewChange, selectedId, items, catalog });
  latest.current = { busy, opening, onOpen, onViewChange, selectedId, items, catalog };
  const remember = (id = latest.current.selectedId) => {
    latest.current.onViewChange?.({
      selectedId: id,
      firstVisibleId: firstId.current,
      library: { ...latest.current.catalog, scrollTop: catalogScroll.current },
    });
  };
  const updateCatalog = (patch: Partial<typeof catalog>, resetScroll = false) => {
    if (resetScroll) {
      catalogScroll.current = 0;
      if (scrollArea.current) scrollArea.current.scrollTop = 0;
    }
    const next = { ...latest.current.catalog, ...patch, scrollTop: catalogScroll.current };
    latest.current.catalog = next;
    setCatalog(next);
    remember();
  };
  const rail = useBookshelfRail({
    count: items.length,
    enabled: !busy && !opening && !library,
    initialIndex: position.current,
    onIndexChange(index) {
      position.current = index;
      firstId.current = latest.current.items[index]?.id ?? null;
      remember();
    },
  });
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
      if (noticeTimer.current) clearTimeout(noticeTimer.current);
      if (closeTimer.current) clearTimeout(closeTimer.current);
    },
    [],
  );
  useEffect(() => {
    if (library) {
      if (scrollArea.current) scrollArea.current.scrollTop = catalogScroll.current;
      panel.current?.focus({ preventScroll: true });
    }
  }, [library]);
  const closeLibrary = () => {
    if (closing || latest.current.opening) return;
    const finish = () => {
      updateCatalog({ open: false });
      setClosing(false);
      requestAnimationFrame(() => libraryButton.current?.focus());
    };
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
    else {
      setClosing(true);
      closeTimer.current = setTimeout(finish, 160);
    }
  };
  const open = (id: string) => {
    if (latest.current.busy || latest.current.opening || closing) return;
    const book = latest.current.items.find((item) => item.id === id);
    if (!book) return;
    if (book.accessStatus === 'LOCKED') {
      setSelectedId(id);
      setNotice({ id, sequence: ++noticeSequence.current });
      if (noticeTimer.current) clearTimeout(noticeTimer.current);
      noticeTimer.current = setTimeout(() => setNotice(null), 2000);
      remember(id);
      return;
    }
    setNotice(null);
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
    latest.current.opening = id;
    setSelectedId(id);
    setOpening(id);
    remember(id);
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) latest.current.onOpen(id);
    else timer.current = setTimeout(() => latest.current.onOpen(id), 240);
  };
  const filtered = items
    .filter(
      (item) =>
        item.name.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()) &&
        (filter === 'ALL' || (item.accessStatus === 'LOCKED' ? 'LOCKED' : 'AVAILABLE') === filter),
    )
    .sort((a, b) => Number(a.accessStatus === 'LOCKED') - Number(b.accessStatus === 'LOCKED'));
  const disabled = busy || !!opening;
  const highlighted = active ?? selectedId;
  const visibleCount = Math.floor(rail.visible);
  const pageCount = Math.max(1, items.length - visibleCount + 1);
  const dotStart = Math.max(0, Math.min(pageCount - 7, rail.index - 3));
  const visibleItems = items.slice(rail.start, rail.end);
  return (
    <section
      className={`student-bookshelf${library ? ' is-library' : ''}${opening ? ' is-opening' : ''}`}
      aria-label="围棋书房"
      aria-busy={busy}
    >
      {!items.length ? (
        <div className="student-bookshelf__message" role={error ? 'alert' : 'status'}>
          <h2>{error ? '书架暂未加载' : busy ? '正在整理书架…' : '书房准备好了'}</h2>
          <p>
            {error || (busy ? '正在读取你的教材与学习进度。' : '老师添加题书后，会显示在这里。')}
          </p>
          {error && (
            <button disabled={busy} onClick={onRefresh}>
              <ReloadIcon aria-hidden="true" />
              重新加载
            </button>
          )}
        </div>
      ) : (
        <>
          {error && (
            <div className="student-bookshelf__refresh-error" role="alert">
              进度刷新失败：{error}
              <button disabled={busy} onClick={onRefresh}>
                重试
              </button>
            </div>
          )}
          <button
            className="student-bookshelf__all"
            ref={libraryButton}
            disabled={disabled || closing}
            aria-expanded={library}
            aria-controls="student-book-catalog"
            onClick={() => (library ? closeLibrary() : updateCatalog({ open: true }))}
          >
            <ReaderIcon aria-hidden="true" />
            <span>全部书籍</span>
            <span className="student-bookshelf__all-count">（{items.length}）</span>
            {library ? (
              <ChevronUpIcon aria-hidden="true" />
            ) : (
              <ChevronDownIcon aria-hidden="true" />
            )}
          </button>
          <div className="student-bookshelf__room" aria-hidden={library} inert={library}>
            <div
              className="student-bookshelf__rail"
              ref={rail.host}
              aria-label="横向浏览电子书"
              tabIndex={-1}
              onDragStart={(event) => event.preventDefault()}
              onKeyDown={(event) => {
                if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
                  event.preventDefault();
                  rail.go(event.key === 'ArrowLeft' ? -1 : 1);
                }
              }}
            >
              <div
                className="student-bookshelf__track"
                style={{
                  width: `calc(100% + ${Math.max(0, items.length - rail.visible)} * var(--shelf-slot))`,
                }}
              >
                {visibleItems.map((item, n) => (
                  <article
                    key={item.id}
                    data-book-id={item.id}
                    className={`student-bookshelf__book${highlighted === item.id ? ' is-active' : ''}${opening === item.id ? ' is-opening' : ''}`}
                    style={{
                      left: `calc(var(--rail-inset) + ${rail.start + n} * var(--shelf-slot))`,
                    }}
                    onPointerEnter={() => setActive(item.id)}
                    onPointerLeave={() => setActive(null)}
                  >
                    <button
                      className="student-bookshelf__select"
                      aria-label={`${item.accessStatus === 'LOCKED' ? '待解锁题书' : '打开电子书'}：${item.name}`}
                      disabled={disabled}
                      onClick={() => open(item.id)}
                      onFocus={(event) => {
                        setActive(item.id);
                        if (event.currentTarget.matches(':focus-visible'))
                          rail.reveal(rail.start + n);
                      }}
                      onBlur={() => setActive(null)}
                    >
                      <Cover
                        item={item}
                        client={client}
                        variant="shelf"
                        shake={notice?.id === item.id ? notice.sequence : 0}
                      />
                    </button>
                  </article>
                ))}
              </div>
            </div>
            <div className="student-bookshelf__cards" aria-label="书籍学习进度">
              {visibleItems.map((item, n) => {
                const index = rail.start + n;
                const visible = index >= rail.index && index < rail.index + visibleCount;
                return (
                  <button
                    key={item.id}
                    data-card-id={item.id}
                    className={`student-bookshelf__card${highlighted === item.id ? ' is-active' : ''}${item.accessStatus === 'LOCKED' ? ' is-locked' : ''}`}
                    style={{
                      left: `calc(${index} * var(--shelf-slot) - var(--shelf-scroll, 0px))`,
                    }}
                    aria-label={`${item.accessStatus === 'LOCKED' ? '查看解锁提示' : '继续练习'}：${item.name}`}
                    aria-hidden={!visible}
                    tabIndex={visible ? 0 : -1}
                    disabled={disabled}
                    onClick={() => open(item.id)}
                    onPointerEnter={() => setActive(item.id)}
                    onPointerLeave={() => setActive(null)}
                    onFocus={() => setActive(item.id)}
                    onBlur={() => setActive(null)}
                  >
                    <div className="student-bookshelf__card-body">
                      <strong className="student-bookshelf__title" title={item.name}>
                        {item.name}
                      </strong>
                      <Progress item={item} />
                    </div>
                    <ReviewCount item={item} />
                  </button>
                );
              })}
            </div>
            <div className="student-bookshelf__navigation">
              <button
                aria-label="前面的书"
                disabled={disabled || rail.index === 0}
                onClick={() => rail.go(-1)}
              >
                <ChevronLeftIcon aria-hidden="true" />
              </button>
              <button
                aria-label="后面的书"
                disabled={disabled || rail.index >= pageCount - 1}
                onClick={() => rail.go(1)}
              >
                <ChevronRightIcon aria-hidden="true" />
              </button>
            </div>
            <div className="student-bookshelf__pagination" aria-label="书架分页">
              {Array.from({ length: Math.min(7, pageCount) }, (_, n) => dotStart + n).map(
                (index) => (
                  <button
                    key={index}
                    aria-label={`第 ${index + 1} 组书籍`}
                    aria-current={index === rail.index ? 'true' : undefined}
                    disabled={disabled}
                    onClick={() => rail.to(index)}
                  >
                    <span />
                  </button>
                ),
              )}
            </div>
          </div>
          {library && (
            <div
              className={`student-bookshelf__library${closing ? ' is-closing' : ''}`}
              id="student-book-catalog"
              ref={panel}
              tabIndex={-1}
              role="region"
              aria-labelledby="student-book-catalog-title"
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  event.preventDefault();
                  closeLibrary();
                }
              }}
            >
              <div className="student-bookshelf__library-heading">
                <h2 id="student-book-catalog-title">
                  全部书籍 <span>{items.length} 本</span>
                </h2>
                <label>
                  <MagnifyingGlassIcon aria-hidden="true" />
                  <input
                    ref={search}
                    type="search"
                    disabled={disabled || closing}
                    aria-label="搜索书名"
                    placeholder="搜索书名"
                    value={query}
                    onChange={(event) => {
                      updateCatalog({ query: event.target.value, limit: 30 }, true);
                    }}
                  />
                </label>
                <button
                  className="student-bookshelf__library-close"
                  aria-label="关闭全部书籍"
                  disabled={disabled || closing}
                  onClick={closeLibrary}
                >
                  <Cross2Icon aria-hidden="true" />
                </button>
              </div>
              <div className="student-bookshelf__filters" role="group" aria-label="题书状态筛选">
                {(['ALL', 'AVAILABLE', 'LOCKED'] as const).map((value, index) => (
                  <button
                    key={value}
                    aria-pressed={filter === value}
                    disabled={disabled || closing}
                    onClick={() => updateCatalog({ filter: value, limit: 30 }, true)}
                  >
                    {['全部', '可练习', '待解锁'][index]}
                  </button>
                ))}
              </div>
              <div
                className="student-bookshelf__library-scroll"
                ref={scrollArea}
                onScroll={(event) => {
                  catalogScroll.current = event.currentTarget.scrollTop;
                  remember();
                }}
              >
                <div className="student-bookshelf__library-grid">
                  {filtered.slice(0, limit).map((item) => (
                    <button
                      className={`student-bookshelf__library-book${opening === item.id ? ' is-opening' : ''}`}
                      key={item.id}
                      data-library-id={item.id}
                      aria-label={`${item.accessStatus === 'LOCKED' ? '查看解锁提示' : '打开电子书'}：${item.name}`}
                      disabled={disabled}
                      onClick={() => open(item.id)}
                    >
                      <span className="student-bookshelf__library-cover">
                        <Cover
                          item={item}
                          client={client}
                          variant="shelf"
                          shake={notice?.id === item.id ? notice.sequence : 0}
                        />
                      </span>
                      <strong>{item.name}</strong>
                      <Progress item={item} compact />
                      <ReviewCount item={item} />
                    </button>
                  ))}
                </div>
                {!filtered.length && (
                  <p role="status" className="student-bookshelf__no-results">
                    {query
                      ? '没有找到这本书，换一个关键词试试。'
                      : filter === 'LOCKED'
                        ? '当前没有待解锁题书。'
                        : '当前没有可练习题书，可在全部书籍中查看待解锁题书。'}
                  </p>
                )}
                {filtered.length > limit && (
                  <button
                    className="student-bookshelf__more"
                    disabled={disabled || closing}
                    onClick={() => updateCatalog({ limit: limit + 30 })}
                  >
                    显示更多（还有 {filtered.length - limit} 本）
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      )}
      {notice && (
        <div
          className="student-bookshelf__lock-notice"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          <LockClosedIcon aria-hidden="true" />
          <span>请联系老师解锁</span>
        </div>
      )}
    </section>
  );
}
